import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { Icon } from "../components/Icon";
import { ProjectInput } from "../components/ProjectInput";
import { trackEvent } from "../telemetry";

export function UploadPage() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [projectName, setProjectName] = useState("");

  const upload = async () => {
    if (files.length === 0) return;
    setBusy(true);
    setError(null);
    const startedAt = performance.now();
    const totalBytes = files.reduce((total, file) => total + file.size, 0);
    trackEvent("upload_started", {
      file_count: files.length,
      total_bytes: totalBytes,
    });
    try {
      const doc = await api.uploadDocument(files, projectName);
      trackEvent("upload_completed", {
        document_id: doc.id,
        file_count: files.length,
        total_bytes: totalBytes,
        duration_ms: Math.round(performance.now() - startedAt),
      });
      navigate(`/docs/${doc.id}/wizard`);
    } catch (exc) {
      trackEvent("upload_failed", {
        file_count: files.length,
        total_bytes: totalBytes,
        status: exc instanceof ApiError ? exc.status : "unknown",
        duration_ms: Math.round(performance.now() - startedAt),
      });
      setError(String(exc));
    } finally {
      setBusy(false);
    }
  };

  const addFiles = (selected: FileList | null) => {
    if (!selected) return;
    const added = Array.from(selected);
    trackEvent("files_selected", {
      added_count: added.length,
      total_count: files.length + added.length,
      added_bytes: added.reduce((total, file) => total + file.size, 0),
    });
    setFiles((current) => [...current, ...added]);
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 24 }}>
      <div className="card card-pad">
        <div className="card-title">Upload an agreement package</div>
        <div className="card-sub mb-4">
          Select the agreement first, then any annexes in chronological order. Each PDF is OCR'd separately and analyzed once as a combined package. PDF only, up to 300 MB per file.
        </div>

        <div style={{ marginBottom: 16 }}>
          <ProjectInput value={projectName} onChange={setProjectName} />
        </div>

        <div
          className={`dropzone${dragging ? " dragging" : ""}`}
          role="button"
          tabIndex={0}
          aria-label="Choose or drop PDF files"
          onClick={(event) => {
            if (event.target !== fileInputRef.current) fileInputRef.current?.click();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              fileInputRef.current?.click();
            }
          }}
          onDragEnter={(event) => {
            event.preventDefault();
            event.stopPropagation();
            dragDepth.current += 1;
            setDragging(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            event.stopPropagation();
            event.dataTransfer.dropEffect = "copy";
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            event.stopPropagation();
            dragDepth.current = Math.max(0, dragDepth.current - 1);
            if (dragDepth.current === 0) setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            event.stopPropagation();
            dragDepth.current = 0;
            setDragging(false);
            addFiles(event.dataTransfer.files);
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            multiple
            style={{ display: "none" }}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
          <div className="dropzone-icon">
            <Icon name="cloudUp" size={28} />
          </div>
          <div className="dropzone-title">
            {files.length === 0 ? "Choose PDF files" : "Add more PDF files"}
          </div>
          <div className="dropzone-sub">Agreement first · annexes next</div>
        </div>

        {files.length > 0 && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 16 }}>
              {files.map((file, index) => (
                <div className="file-card" key={`${file.name}-${file.size}-${index}`}>
                  <div className="file-icon">
                    <span className="ext">PDF</span>
                  </div>
                  <div className="file-meta">
                    <div className="file-name">
                      {index === 0 ? "Agreement" : `Annex ${index}`} · {file.name}
                    </div>
                    <div className="file-info">{(file.size / 1024 / 1024).toFixed(2)} MB</div>
                  </div>
                  <button
                    className="icon-btn"
                    aria-label={`Remove ${file.name}`}
                    onClick={() => {
                      trackEvent("file_removed", {
                        position: index,
                        file_size: file.size,
                      });
                      setFiles((current) => current.filter((_, i) => i !== index));
                    }}
                  >
                    <Icon name="x" size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex" style={{ justifyContent: "flex-end", marginTop: 18 }}>
              <button className="btn btn-primary btn-lg" disabled={busy} onClick={upload}>
                {busy ? "Uploading…" : "Upload and start analysis"}{" "}
                <Icon name="arrowRight" size={14} />
              </button>
            </div>
          </>
        )}

        {error && (
          <div style={{ color: "#b53a2c", fontSize: 13, marginTop: 10 }}>{error}</div>
        )}
      </div>
    </div>
  );
}
